import type { DcrhcpInput, Hcp } from '@/core/graphql/generated/types';
import { parseYesNoBoolean } from '@/utils/parsers/parseYesNoBoolean';

import type { mapHcpToFormDefaultsIn } from '../_utils/mapHcpToFormDefaultsIn';
import { getNetworkFieldValue } from '../_utils/networkApiMappers/getNetworkFieldValue';
import { getNumberFieldValue } from '../_utils/networkApiMappers/getNumberFieldValue';
import { mapAddressesToNetworkApi } from '../_utils/networkApiMappers/mapAddressToNetworkApi';
import { mapAffiliationToNetworkApi } from '../_utils/networkApiMappers/mapAffiliationToNetworkApi';
import { mapLicensesToNetworkApi } from '../_utils/networkApiMappers/mapLicensesToNetworkApi';
import { resolveLocalSpec } from '../_utils/networkApiMappers/resolveLocalSpec';
import { resolvePicklistId } from '../_utils/networkApiMappers/resolvePicklistId';

import type { useDcrSetup } from './useDcrSetup';
import { usePicklistValues } from './usePicklistValues';

type FormDefaults = ReturnType<typeof mapHcpToFormDefaultsIn>;

type SpecialtyItem = {
	vid_V?: string;
	id?: string;
	isPrimary?: boolean;
	fieldName?: string;
};

type Props = {
	payload: Partial<FormDefaults>;
	vid_V: Hcp['vid_V'] | undefined;
	countryId: string | undefined;
	picklists: ReturnType<typeof usePicklistValues>['picklists'];
	hcp: Partial<Hcp> | null;
	unavailableFields: ReturnType<typeof useDcrSetup>['unavailableFields'];
};

export const mapHcpToNetworkApiFieldNames = ({
	payload,
	vid_V,
	countryId,
	unavailableFields,
	picklists: {
		languageOptions,
		hcpGenderOptions,
		statusOptions,
		specialtyLocalOptions,
		hcpTypeOptions,
		localHcpTypeOptions,
		relationshipTypeOptions,
		licenseStatusOptions,
		countryOptions,
		stateOptions,
		relationStatusOptions
	},
	hcp
}: Props): Partial<DcrhcpInput> => {
	const language = payload?.languageCda_V;
	const resolvedLanguage = languageOptions?.find(
		l => l.name === language || l.id === language
	);

	const gender = payload?.gender_V;
	const resolvedGender = hcpGenderOptions?.find(
		g => g.name === gender || g.id === gender
	);

	const status = payload?.statusCda_V;
	const resolvedStatus = statusOptions?.find(
		s => s.name === status || s.id === status
	);

	// Helper: Resolve ID
	const getId = (s: SpecialtyItem) => {
		const rawValue = s.id ?? s.vid_V;
		if (!rawValue) return undefined;
		const option = specialtyLocalOptions?.find(
			spec => spec.id === rawValue || spec.name === rawValue
		);
		return option?.id ?? rawValue;
	};

	const hcpType = payload?.hcpType;
	const resolvedHcpType = hcpTypeOptions?.find(
		ht => ht.name === hcpType || ht.id === hcpType
	);

	const localHcpType = payload?.hcpTypeLocal_VId;
	const resolvedLocalHcpType = localHcpTypeOptions?.find(
		ht => ht.name === localHcpType || ht.id === localHcpType
	);

	// const addressesArray = toArray(payload.addresses);
	// const licensesArray = toArray(payload.licenses);
	// const parentHcosArray = toArray(payload.parenthcos);

	const mappedValues = {
		// Name Section
		firstNameCda_V: getNetworkFieldValue(payload.firstNameCda_V),
		lastNameCda_V: getNetworkFieldValue(payload.lastNameCda_V),
		middleNameCda_V: getNetworkFieldValue(payload.middleNameCda_V),
		prefixCda_V: getNetworkFieldValue(payload.prefixCda_V),
		suffixCda_V: getNetworkFieldValue(payload?.suffixCda_V),

		// Demographics Section
		languageCda_VId: resolvePicklistId(language, resolvedLanguage?.id),
		gender_VId: resolvePicklistId(gender, resolvedGender?.id),
		// birthYear input clears via setValue(undefined, { shouldDirty: true }),
		// so the key is present in dirty payload with an undefined value. Use
		// "in" to distinguish "not touched" from "touched but cleared".
		birthYear_V:
			'birthYear_V' in payload
				? (getNumberFieldValue(payload.birthYear_V) ?? null)
				: undefined,
		dataPrivacyOptOut_V:
			parseYesNoBoolean(payload.dataPrivacyOptOut_V) ?? undefined,
		// gradSchool_V: getNetworkFieldValue(payload.gradSchool_V),
		// gradYear_V: parseNumber(payload.gradYear_V) ?? undefined,

		// Contact Section
		officePhoneCda_V: getNetworkFieldValue(payload.officePhoneCda_V),
		phone2_V: getNetworkFieldValue(payload.phone2_V),
		faxCda_V: getNetworkFieldValue(payload.faxCda_V),
		emailCda_V: getNetworkFieldValue(payload.emailCda_V),
		email2_V: getNetworkFieldValue(payload.email2_V),
		email3_V: getNetworkFieldValue(payload.email3_V),
		email4_V: getNetworkFieldValue(payload.email4_V),
		email5_V: getNetworkFieldValue(payload.email5_V),
		websiteCda_V: getNetworkFieldValue(payload.websiteCda_V),

		// Professional Section
		statusCda_VId: resolvePicklistId(status, resolvedStatus?.id),
		hcpTypeCda_VId: resolvePicklistId(hcpType, resolvedHcpType?.id),
		hcpTypeLocal_VId: resolvePicklistId(localHcpType, resolvedLocalHcpType?.id),
		nhidCda_V: getNetworkFieldValue(payload?.nhidCda_V),
		nhid2_V: getNetworkFieldValue(payload?.nhid2_V),
		// Arrays: if key is present in dirty payload, treat missing/empty first-item
		// id (autocomplete clear emits "", row removal emits []) as a clear (null).
		// If absent from payload, field was never touched.
		degree1Cda_VId:
			'degrees' in payload
				? (getNetworkFieldValue(payload.degrees?.[0]?.id) ?? null)
				: undefined,
		spec1Cda_VId:
			'hcpSpecialties' in payload
				? (getNetworkFieldValue(payload.hcpSpecialties?.[0]?.id) ?? null)
				: undefined,
		// prescriberCda_V: parseYesNoBoolean(payload.prescriberCda_V),
		// inCurrentPecos_V: parseYesNoBoolean(payload.inCurrentPecos_V),
		// telehealth_V: parseYesNoBoolean(payload.telehealth_V),

		// Local specialties
		spec1Local_VId: resolveLocalSpec(
			payload.allSpecLocal_VId,
			0,
			hcp?.primaryLocalSpecialty?.code,
			getId
		),
		spec2Local_VId: resolveLocalSpec(
			payload.allSpecLocal_VId,
			1,
			hcp?.secondaryLocalSpecialty?.code,
			getId
		),

		// Nested entities
		addresses_V: payload?.addresses
			? mapAddressesToNetworkApi({
					addresses: payload?.addresses,
					countryOptions,
					stateOptions,
					statusOptions,
					unavailableFields
				})
			: undefined,

		licenses_V: payload?.licenses
			? mapLicensesToNetworkApi({
					licenses: payload?.licenses,
					licenseStatusOptions,
					unavailableFields
				})
			: undefined,

		parentHcos_V: payload?.parenthcos
			? mapAffiliationToNetworkApi({
					affiliations: payload?.parenthcos,
					relationshipTypeOptions,
					relationStatusOptions,
					unavailableFields
				})
			: undefined,

		// Unused fields
		primaryCountry_V: undefined,
		hcpStatus_VId: undefined
	};

	const map =
		vid_V !== undefined
			? { ...mappedValues, vid_V }
			: { ...mappedValues, primaryCountry_V: countryId };

	type MapType = typeof map;
	type Key = keyof MapType;

	const keys = Object.keys(map) as Key[];

	const result = keys.reduce((acc, key) => {
		// Strip fields found in unavailableFields list
		if (unavailableFields?.includes(key as string)) {
			return acc;
		}

		const value = map[key];
		if (value !== undefined) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(acc as any)[key] = value;
		}
		return acc;
	}, {} as Partial<MapType>);

	return result as Partial<DcrhcpInput>;
};
